import { UiArrowExpandIcon } from '@octane/spark'
import styled, { css } from 'styled-components'

export type DealOwnerFieldSize = 'compact' | 'full'

const useSecondaryFont = css`
    font-family: ${({ theme }) => theme.fonts.secondary};
`

const sizeTokens = {
    compact: {
        triggerWidth: '170px',
        triggerHeight: '36px',
        triggerPadding: '0 10px',
        fontSize: '14px',
        fontWeight: '400',
        popoverWidth: '220px',
        optionFontSize: '14px',
        optionPadding: '10px 12px',
    },
    full: {
        triggerWidth: '100%',
        triggerHeight: '50px',
        triggerPadding: '0 16px',
        fontSize: '16px',
        fontWeight: '700',
        popoverWidth: '100%',
        optionFontSize: '16px',
        optionPadding: '12px 16px',
    },
} as const

export const Card = styled.div`
    background-color: white;
    border: 1px solid #d5d9e0;
    border-radius: 8px;
    margin-bottom: 20px;
    padding: 20px 22px;
`

export const HorizontalLayout = styled.div`
    align-items: center;
    display: flex;
    gap: 32px;
    ${useSecondaryFont}
`

export const VerticalLayout = styled.div`
    display: flex;
    flex-direction: column;
    ${useSecondaryFont}
`

export const InlineLayout = styled.div`
    align-items: center;
    display: flex;
    gap: 8px;
    ${useSecondaryFont}
`

export const LabelArea = styled.div`
    flex: 0 0 280px;
`

export const DropdownArea = styled.div`
    display: flex;
    flex: 1 1 auto;
    justify-content: center;
`

export const InlineLabel = styled.span`
    color: var(--color-charcoal-70, #6a6e76);
    font-size: 16px;
    font-weight: 400;
    line-height: 20px;
    ${useSecondaryFont}
`

export const Wrapper = styled.div<{ $size: DealOwnerFieldSize }>`
    position: relative;
    width: ${({ $size }) => sizeTokens[$size].triggerWidth};
    ${useSecondaryFont}
`

export const Trigger = styled.button<{
    disabled?: boolean
    $size: DealOwnerFieldSize
}>`
    align-items: center;
    background: ${({ disabled, theme }) =>
        disabled ? theme.colors.form.moonGrey : theme.colors.monochrome.white};
    border: 1px solid ${({ theme }) => theme.colors.monochrome.graphite};
    border-radius: 5px;
    box-sizing: border-box;
    color: ${({ theme }) => theme.colors.monochrome.jetlineBlack};
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    display: flex;
    height: ${({ $size }) => sizeTokens[$size].triggerHeight};
    justify-content: space-between;
    padding: ${({ $size }) => sizeTokens[$size].triggerPadding};
    text-align: left;
    width: 100%;
    ${useSecondaryFont}

    &:focus {
        border-color: ${({ theme }) => theme.colors.brand.primary};
        outline: none;
    }
`

export const TriggerText = styled.span<{ $size: DealOwnerFieldSize }>`
    color: ${({ theme }) => theme.colors.monochrome.jetlineBlack};
    flex: 1;
    font-size: ${({ $size }) => sizeTokens[$size].fontSize};
    font-weight: ${({ $size }) => sizeTokens[$size].fontWeight};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    ${useSecondaryFont}
`

export const ChevronIcon = styled(UiArrowExpandIcon)`
    height: 12px;
    margin-left: 12px;
    width: 12px;
`

export const Popover = styled.div<{ $size: DealOwnerFieldSize }>`
    background: #ffffff;
    border: 1px solid var(--color-charcoal-40, #d6d7da);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    left: 0;
    margin-top: 4px;
    max-height: 240px;
    min-width: 100%;
    overflow-y: auto;
    position: absolute;
    top: 100%;
    width: ${({ $size }) => sizeTokens[$size].popoverWidth};
    z-index: 1000;
    ${useSecondaryFont}
`

export const Option = styled.button<{ $size: DealOwnerFieldSize }>`
    background: transparent;
    border: none;
    color: var(--color-charcoal-90, #30333a);
    cursor: pointer;
    display: block;
    font-size: ${({ $size }) => sizeTokens[$size].optionFontSize};
    overflow: hidden;
    padding: ${({ $size }) => sizeTokens[$size].optionPadding};
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
    ${useSecondaryFont}

    &:hover,
    &:focus {
        background: var(--color-cloud-30, #f3f3f3);
        outline: none;
    }
`

export const WriteInForm = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    ${useSecondaryFont}
`

export const SearchInputWrapper = styled.div`
    background: ${({ theme }) => theme.colors.monochrome.white};
    border-bottom: 1px solid var(--color-charcoal-40, #d6d7da);
    padding: 8px 10px;
    position: sticky;
    top: 0;
    z-index: 1;
`

export const SearchInput = styled.input`
    border: 1px solid var(--color-charcoal-40, #d6d7da);
    border-radius: 4px;
    box-sizing: border-box;
    padding: 4px 8px;
    width: 100%;
    ${useSecondaryFont}

    &:focus {
        border-color: var(--color-charcoal-60, #a6a9b0);
        outline: none;
    }
`

export const NoMatchesMessage = styled.div`
    color: ${({ theme }) => theme.colors.monochrome.graphite};
    font-size: 14px;
    font-style: italic;
    padding: 12px 16px;
    text-align: center;
    ${useSecondaryFont}
`

export const CancelLinkWrapper = styled.div`
    align-self: center;
`

export const ErrorBanner = styled.div`
    background: var(--color-cranberry-10, #fdf2f2);
    border: 1px solid var(--color-cranberry-50, #d1435b);
    border-radius: 4px;
    color: var(--color-cranberry-90, #6a0e1f);
    font-size: 14px;
    margin-top: 6px;
    padding: 6px 10px;
    ${useSecondaryFont}
`

export const FieldLabel = styled.div`
    color: ${({ theme }) => theme.colors.monochrome.ravenSub};
    font-size: 14px;
    font-weight: 700;
    line-height: 16px;
    margin-bottom: 5px;
    ${useSecondaryFont}
`

/*
 * Floating "NEW" pin anchored to the top-right corner of the dropdown
 * trigger. Negative offsets let it overlap the corner. pointer-events:
 * none so it never intercepts clicks on the underlying dropdown.
 *
 * Padding + font-size on Spark's <Label> inner spans are overridden so
 * the pin renders compact — resizing via layout properties instead of
 * transform: scale() keeps the visual and layout box aligned.
 */
export const NewBadgePin = styled.span`
    pointer-events: none;
    position: absolute;
    right: -8px;
    top: -8px;
    z-index: 2;

    /*
     * Spark's <Label size="small"> forces height: 26px on its outer span
     * and its background color comes from the theme's alert palette.
     * Override height + padding to shrink the shape while keeping the
     * font size, and force background/text colors explicitly so
     * "spotlight" always renders as the intended blue with white text
     * (avoids any theme-cascade surprises that read as near-black).
     */
    & > span {
        background-color: #006adb;
        color: #ffffff;
        height: auto;
        max-height: none;
        min-height: 0;
        padding: 0 5px;
    }

    & > span > span {
        color: #ffffff;
        font-size: 9px;
        letter-spacing: 0.02em;
        line-height: 1.4;
    }
`

export const HelperText = styled.div`
    color: var(--color-charcoal-70, #6a6e76);
    font-size: 14px;
    line-height: 22px;
    margin-bottom: 8px;
    ${useSecondaryFont}
`
